import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAppSelector, useAppDispatch } from 'modules/state-mgmt/store';
import useLoadAllData from 'hooks/useLoadAllData';
import { getFirstIncompleteStep } from 'modules/views/shared/Wizard/utils';
import { declineProject } from 'modules/state-mgmt/cache';
import { setTemp, fetchAssignedCompanies, getRevokeBadgeInfo } from 'modules/state-mgmt/onboarding';

import { ROUTES } from 'constants/routes';
import { enqueueSnackbar } from 'notistack';
import { prettifyErrorResponse } from 'utils';
import { useTranslation } from 'react-i18next';

export const useProjectActions = ({ selectedProject, refreshProjects, onDeclineSuccess }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isViewDocumentsLoading, setIsViewDocumentsLoading] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [{ hasLoaded, isLoading: isLoadingData, errors }, loadAllData] = useLoadAllData();
  const { workerId, hasSignatureUrl } = useAppSelector(state => ({
    onboarding: state.onboarding,
    profileFieldConstraintsMap: state.cache.profileFieldConstraintsMap,
    canCreateUnverifiedWorkers: state.cache.canCreateUnverifiedWorkers,
    workerId: state.onboarding?.id || state.user?.workerId,
    hasSignatureUrl: !!(state.onboarding as any)?.signatureUrl,
  }));
  const { t } = useTranslation();

  // Employers to open the wizard with, resolved by `loadProjectWizardData` before `loadAllData` runs.
  const wizardEmployersRef = useRef<{ label: string; value: string }[]>([]);

  useEffect(() => {
    if (hasLoaded) {
      setIsLoading(false);
      dispatch(
        setTemp({
          project: { label: selectedProject?.project?.name || '', value: selectedProject?.project?.id || '' },
          employers: wizardEmployersRef.current,
          visitedWizardSteps: [],
        }),
      );
      if (isViewDocumentsLoading) {
        setIsViewDocumentsLoading(false);
        navigate(ROUTES.CONSENT_FORM_INFORMATION.path);
      } else {
        navigate(ROUTES.PERSONAL_INFORMATION.path);
      }
    }
    if (!isLoadingData && !hasLoaded && errors.length > 0) {
      setIsLoading(false);
      setIsViewDocumentsLoading(false);
      errors.forEach(error => enqueueSnackbar(prettifyErrorResponse(error, true), { variant: 'error' }));
    }
  }, [hasLoaded, isLoadingData, errors]);

  /**
   * A card only lists the companies matching its own tab, so a project held as accepted with one
   * company and invited with another shows up as two cards, each with half the employers. The
   * wizard needs all of them — the consent form only accepts the project for companies in
   * `temp.employers` — so read the project-scoped list instead, as `onRegistration` does.
   */
  const loadProjectWizardData = async () => {
    try {
      const assignedCompanies = await dispatch(
        fetchAssignedCompanies({
          projectId: selectedProject?.project?.id || '',
          languageCode: 'en',
          workerId: workerId || '',
        }),
      ).unwrap();
      wizardEmployersRef.current =
        assignedCompanies?.length > 0
          ? assignedCompanies.map(c => ({ label: c.name || '', value: c.id || '' }))
          : selectedProject?.companies?.map(e => ({ label: e.company?.name || '', value: e.company?.id || '' })) || [];
      loadAllData({
        workerId,
        projectId: selectedProject?.project?.id,
        employers: wizardEmployersRef.current.map(e => e.value),
      });
    } catch (error) {
      setIsLoading(false);
      setIsViewDocumentsLoading(false);
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    }
  };

  const onViewProfile = () => {
    setIsLoading(true);
    loadProjectWizardData();
  };

  const onViewDocuments = () => {
    setIsLoading(true);
    setIsViewDocumentsLoading(true);
    loadProjectWizardData();
  };

  const onDecline = async (selectedCompanies: { label: string; value: string }[]) => {
    const companyIds = selectedCompanies.map(company => company.value);
    if (!selectedProject?.project?.id || !workerId || companyIds?.length < 1) {
      return;
    }

    setIsLoading(true);

    try {
      // Call declineProject for each selected company
      for (const companyId of companyIds) {
        await dispatch(
          declineProject({
            projectId: selectedProject.project.id,
            workerId: workerId,
            companyId: companyId,
          }),
        ).unwrap();
      }

      // Refresh the project list after successful decline
      if (refreshProjects) {
        await refreshProjects();
      }
      if (onDeclineSuccess) {
        onDeclineSuccess();
      }
    } catch (error) {
      enqueueSnackbar('Failed to decline project.', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const onAddEmployer = async () => {
    if (!workerId) {
      return;
    }
    try {
      setIsLoading(true);
      const { isRevoked = false } = await dispatch(
        getRevokeBadgeInfo({
          workerId: workerId,
          projectId: selectedProject?.project?.id || '',
          languageCode: 'en',
        }),
      ).unwrap();
      setIsLoading(false);
      if (isRevoked) {
        enqueueSnackbar(t('projectList.errors.registrationErrorBadgeRevoked'), { variant: 'error' });
        return;
      }
      navigate(
        `${ROUTES.SELECT_PROJECT.path}?projectId=${selectedProject?.project?.id}&projectName=${encodeURIComponent(selectedProject?.project?.name || '')}`,
        {
          state: {
            companies: selectedProject?.companies?.map(e => e.company) || [],
            addEmployerFlow: true,
          },
        },
      );
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const onRegistration = async () => {
    if (!workerId) {
      return;
    }
    try {
      setIsLoading(true);
      const { isRevoked = false } = await dispatch(
        getRevokeBadgeInfo({
          workerId: workerId,
          projectId: selectedProject?.project?.id || '',
          languageCode: 'en',
        }),
      ).unwrap();
      if (isRevoked) {
        enqueueSnackbar(t('projectList.errors.registrationErrorBadgeRevoked'), { variant: 'error' });
        setIsLoading(false);
        return;
      }
      const assignedCompanies = await dispatch(
        fetchAssignedCompanies({
          projectId: selectedProject?.project?.id || '',
          languageCode: 'en',
          workerId: workerId || '',
        }),
      ).unwrap();

      loadAllData({
        workerId: workerId,
        projectId: selectedProject?.project?.id,
        employers: assignedCompanies.map(c => c.id),
        onlyImportantApis: false,
      }).then(() => {
        if (assignedCompanies.length > 0) {
          const preSelectedEmployers = assignedCompanies.map(c => ({
            label: c.name,
            value: c.id,
          }));
          if (preSelectedEmployers.length > 0) {
            dispatch(
              setTemp({
                project: { label: selectedProject?.project?.name || '', value: selectedProject?.project?.id || '' },
                employers: preSelectedEmployers,
                addEmployerFlow: true,
                visitedWizardSteps: [],
              }),
            );
            if (hasSignatureUrl) {
              navigate(ROUTES.PERSONAL_INFORMATION.path);
            } else {
              navigate(ROUTES.TERMS_AND_CONDITIONS.path);
            }
          }
        }
      });
    } catch (error) {
      enqueueSnackbar(prettifyErrorResponse(error), { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading: isLoading,
    onViewProfile,
    onViewDocuments,
    onDecline,
    onAddEmployer,
    onRegistration,
  };
};
